import { GameConfig } from './models/game-config.model';
import { CardModel, CardState, GameViewModel, HandModel, HandState, SideModel, SideState } from './models/game-view.model';
import { Card } from '../models/communication/cards.enum';

/**
 * Transforms the config (`GameConfig`) to the (`GameViewModel`) data
 * used by `GameBase` and the component templates to generate the UI.
 * The data is organized as a hierarchy of one or more elements:
 *
 * -> Side
 * ---> Hand
 * -----> Card
 */

export const gameViewData = {
    getDataFromConfig(config: GameConfig): GameViewModel {
        const title = { text: '', isSpecial: false };
        const cards = this.generateCards(config);
        const hands = this.generateHands(config, cards);
        const sides = this.generateSides(config, hands);

        return this.addParentRefs({
            title,
            cards,
            hands,
            sides
        });
    },

    generateCards: (config: GameConfig): CardModel[] => {
        const cards = [];

        for (const sideData of config.sides) {
            for (const handData of sideData.hands) {
                for (const cardData of handData.cards) {
                    // We need to assert because the property `parentHand` is missing.
                    // It will be added in `addParentRefs`.
                    cards.push({
                        position: cardData.position,
                        state: cardData.state ?? CardState.Empty,
                        value: Card.Unknown,
                        isRotated: cardData.isRotated ?? false,
                        isCompact: cardData.isCompact ?? false
                    } as CardModel);
                }
            }
        }

        cards.sort((a, b) => a.position - b.position);
        return cards;
    },

    generateHands: (config: GameConfig, cards: CardModel[]): HandModel[] => {
        const hands = [];

        for (const [sideIndex, sideData] of config.sides.entries()) {
            for (const handData of sideData.hands) {
                // We need to assert because the property `parentSide` is missing.
                // It will be added in `addParentRefs`.
                hands.push({
                    label: handData.label,
                    state: HandState.Default,
                    sideIndex,
                    amountKey: handData.amountKey,
                    cards: handData.cards.map((card) => cards[card.position]),
                    amount: { value: 0, visible: false }
                } as HandModel);
            }
        }

        return hands;
    },

    generateSides: (config: GameConfig, hands: HandModel[]): SideModel[] => {
        return config.sides.map((sideData, sideIndex) => {
            return {
                position: sideData.position,
                state: SideState.Default,
                hands: hands.filter((hand) => hand.sideIndex === sideIndex)
            };
        });
    },

    addParentRefs: (data: GameViewModel): GameViewModel => {
        for (const side of data.sides) {
            for (const hand of side.hands) {
                hand.parentSide = side;
                for (const card of hand.cards) {
                    card.parentHand = hand;
                }
            }
        }
        return data;
    }
};
